import {
  EventPayloadFactory,
  NextEpisodeEvent,
  EventData,
} from '../EventPayloadFactory/EventPayloadFactory'
import { ProgressEvent } from '../timeEventListener'

class EventHandlers {
  constructor(
    private readonly send: (data: EventData) => void,
    private readonly payloadFactory: EventPayloadFactory,
  ) {}

  onPlay = () => this.send(this.payloadFactory.getEvent('player_play'))

  onPause = () => this.send(this.payloadFactory.getEvent('player_pause'))

  onResume = () => this.send(this.payloadFactory.getEvent('player_resume'))

  onFirstFrame = () => this.send(this.payloadFactory.getEvent('player_start'))

  onTimeEvent = (eventName: ProgressEvent) => {
    this.send(this.payloadFactory.getTimeEvent(eventName))
  }

  onRewind = () => {
    this.send(this.payloadFactory.getEvent('player_rewind'))
  }

  onBuffering = ({
    reason,
  }: {
    reason: 'loading' | 'complete' | 'stalled' | 'error'
  }) => {
    if (reason === 'stalled') {
      this.send(this.payloadFactory.getEvent('player_buffering'))
    }
  }

  onAbort = () => {
    this.send(this.payloadFactory.getEvent('player_abort_stream'))
  }

  onTimelineDragRelease = (eventPayload: {
    position: number
    offset: number
  }) => {
    this.send(this.payloadFactory.getEvent('player_seek', eventPayload))
  }

  onPlayerStepBackward = ({
    position,
    offset,
  }: {
    position: number
    offset: number
  }) => {
    this.send(
      this.payloadFactory.getEvent('player_step_backward', {
        position,
        offset,
      }),
    )
  }

  onPlayerStepForward = ({
    position,
    offset,
  }: {
    position: number
    offset: number
  }) => {
    this.send(
      this.payloadFactory.getEvent('player_step_forward', {
        position,
        offset,
      }),
    )
  }

  onCaptionsChanged = ({ from, to }: { from: string; to: string }) => {
    if (from !== to) {
      this.send(
        this.payloadFactory.getEvent('player_change_subtitles', {
          from,
          to,
        }),
      )
    }
  }

  onAutoPlayAbort = (nextEpisodeEvent: NextEpisodeEvent) => {
    this.send(
      this.payloadFactory.getEvent('player_autoplay_abort', nextEpisodeEvent),
    )
  }

  onAutoPlayClickPlay = (nextEpisodeEvent: NextEpisodeEvent) => {
    this.send(
      this.payloadFactory.getEvent(
        'player_autoplay_click_play',
        nextEpisodeEvent,
      ),
    )
  }

  onFullscreen = ({ fullscreen }: { fullscreen: boolean }) => {
    const event = fullscreen
      ? 'player_enter_fullscreen'
      : 'player_exit_fullscreen'
    this.send(this.payloadFactory.getEvent(event))
  }

  onQualityChange = e => {
    if (e.mode === 'manual' && e.reason === 'api') {
      const quality = e.level.label
      this.send(
        this.payloadFactory.getEvent('player_change_quality', { quality }),
      )
    }
  }

  onChapterClick = () => {
    this.send(this.payloadFactory.getEvent('player_toggle_chapters'))
  }
}

export default EventHandlers
